import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Receipt } from "lucide-react";
import { useStore } from "../lib/store";
import { appPath } from "../app/routes";
import { Button, Dialog, EmptyState, PageHead, Tabs } from "../ui";
import { CompactSelect } from "../ui/CompactSelect";
import { Drawer } from "../ui/Dialog";
import { ReceiptDialog } from "./PerfilDialogs";
import { formatCents } from "../features/finance/format";
import {
  INVOICE_STATUS_LABEL,
  contractCutoff,
  invoiceBalanceCents,
  invoiceStatus,
  paidCents,
  type FinanceContract,
  type FinanceInvoice,
  type FinanceReceipt,
  type InvoiceStatus,
} from "../features/finance/contracts";
import { readPreference } from "../features/preferences/contracts";
import { civilDate, invoiceNotices, type Notice } from "../features/notifications/feed";
import "./portal.css";

/**
 * Portal do cliente — prévia interna.
 *
 * O protótipo mostra "o que o cliente veria". Aqui isso é exatamente uma
 * prévia: não existe acesso externo, conta, convite nem autenticação de
 * cliente (§9, cadastro público desativado). O que o protótipo simulava
 * — convidar, ativar e o cliente escrevendo de volta — aparece visível e
 * desabilitado dizendo por quê (§18), em vez de gravar um estado que não
 * corresponde a nada.
 *
 * O que continua real: contratos, faturas, saldos e recebimentos vêm do
 * domínio Financeiro, e "registrar recebimento" é o comando de verdade, o
 * mesmo do perfil do lead — inclusive fechando a venda no primeiro.
 */

type PortalView = "resumo" | "cobrancas" | "mensagens" | "avisos";

const VIEWS: Array<{ id: PortalView; label: string }> = [
  { id: "resumo", label: "Resumo" },
  { id: "cobrancas", label: "Contratos e cobranças" },
  { id: "mensagens", label: "Mensagens" },
  { id: "avisos", label: "Notificações" },
];

const STATUS_FILTERS = [
  { value: "all", label: "Todas" },
  { value: "open", label: "Em aberto" },
  { value: "late", label: "Atrasadas" },
  { value: "paid", label: "Recebidas" },
  { value: "cancelled", label: "Canceladas" },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]["value"];

/** Etapa da demonstração do acesso; nenhuma delas é conta ou sessão. */
type AccessState = "none" | "invited" | "active";

const ACCESS_LABEL: Record<AccessState, string> = {
  none: "Acesso disponível para prévia",
  invited: "Convite simulado disponível",
  active: "Ativação simulada concluída",
};

const CHANNEL_LABEL: Record<string, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
};

function brDate(date: string): string {
  return date.split("-").reverse().join("/");
}

/** Mês de competência da parcela, como no protótipo: `MM/AAAA`. */
function competence(date: string): string {
  return `${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

function hourOf(instant: string): string {
  return new Date(instant).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PortalPage() {
  const { leadId = "" } = useParams();
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [invoiceOpen, setInvoiceOpen] = useState<string | null>(null);
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);

  const today = civilDate(new Date().toISOString());
  const lead = (store.leads ?? []).find((item) => item.id === leadId);
  const client = Boolean(lead?.clientSince) && !lead?.archived;

  const contracts = useMemo(
    () => (store.contracts ?? []).filter((contract) => contract.leadId === leadId),
    [store.contracts, leadId],
  );
  const invoices = useMemo(
    () =>
      (store.invoices ?? [])
        .filter((invoice) => contracts.some((contract) => contract.id === invoice.contractId))
        .slice()
        .sort((a, b) => a.dueOn.localeCompare(b.dueOn)),
    [store.invoices, contracts],
  );
  const receipts = store.receipts ?? [];

  const threads = useMemo(
    () =>
      (store.communicationThreads ?? [])
        .filter((thread) => thread.leadId === leadId && !thread.trashedAt)
        .slice()
        .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt)),
    [store.communicationThreads, leadId],
  );
  const messages = store.communicationMessages ?? [];

  const portal = readPreference(store.userPreferences ?? [], "portal").value;
  const access: AccessState = portal.access[leadId] ?? "none";

  const advanceAccess = async () => {
    if (savingAccess) return;
    setSavingAccess(true);
    await store.setPreference({
      key: "portal",
      value: {
        ...portal,
        access: { ...portal.access, [leadId]: access === "invited" ? "active" : "invited" },
      },
    });
    setSavingAccess(false);
    setAccessOpen(false);
  };

  const view = VIEWS.find((item) => item.id === params.get("aba"))?.id ?? "resumo";
  const status = (STATUS_FILTERS.find((item) => item.value === params.get("situacao"))?.value ??
    "all") as StatusFilter;
  const threadId = params.get("conversa") ?? threads[0]?.id ?? "";

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  if (!lead || !client) {
    return (
      <div className="cx-portal">
        <PageHead
          title="Prévia indisponível"
          sub="A área do cliente existe a partir do primeiro recebimento: até lá o cadastro ainda é um lead em negociação."
          actions={
            <Button
              onClick={() => navigate(lead ? `${appPath("leads")}/${lead.id}` : appPath("clientes"))}
            >
              Voltar à operação
            </Button>
          }
        />
      </div>
    );
  }

  const activeContracts = contracts.filter(
    (contract) => contract.kind === "monthly" && contractCutoff(contract) >= today,
  );
  const openInvoices = invoices.filter(
    (invoice) => !invoice.cancelledAt && invoiceBalanceCents(invoice, receipts) > 0,
  );
  const next = openInvoices[0];

  // Só as regras de cobrança: tarefas, leads parados e incidentes são da
  // operação e não teriam sentido na visão do cliente.
  const notices = invoiceNotices({
    today,
    leads: [lead],
    memberships: store.memberships ?? [],
    tasks: [],
    invoices,
    receipts,
  });

  const recent = messages
    .filter((message) => threads.some((thread) => thread.id === message.threadId))
    .slice()
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .slice(-6);

  const openInvoice = invoiceOpen
    ? invoices.find((invoice) => invoice.id === invoiceOpen)
    : undefined;
  const receiptInvoice = receiptFor
    ? invoices.find((invoice) => invoice.id === receiptFor)
    : undefined;

  return (
    <div className="cx-portal">
      <div className="cx-portal-banner">
        <span>
          Área do cliente · demonstração local de {lead.name}
        </span>
        <Button onClick={() => navigate(`${appPath("leads")}/${lead.id}`)}>
          Voltar à operação
        </Button>
      </div>

      <PageHead
        title={lead.company || lead.name}
        sub={activeContracts[0]?.description ?? "Seu relacionamento com o CosmosLeads"}
      />

      <Tabs
        items={VIEWS}
        value={view}
        label="Área do cliente"
        controls="cx-portal-conteudo"
        onChange={(id) => setParam("aba", id === "resumo" ? null : id)}
      />

      <div id="cx-portal-conteudo" className="cx-portal-content">
        {view === "resumo" && (
          <>
            <dl className="cx-portal-strip">
              <div>
                <dt>Contrato</dt>
                <dd className="num">{activeContracts[0]?.description ?? "Sem contrato ativo"}</dd>
              </div>
              <div>
                <dt>Próxima mensalidade</dt>
                <dd className="num">
                  {next ? formatCents(invoiceBalanceCents(next, receipts)) : "Sem saldo previsto"}
                </dd>
              </div>
              <div>
                <dt>Vencimento</dt>
                <dd className="num">{next ? brDate(next.dueOn) : "—"}</dd>
              </div>
              <div>
                <dt>Situação</dt>
                <dd className="num">
                  {next ? INVOICE_STATUS_LABEL[invoiceStatus(next, receipts, today)] : "Em dia"}
                </dd>
              </div>
            </dl>

            <div className="cx-portal-grid">
              <section className="cx-card">
                <div className="card-title">
                  <h2>Comunicações recentes</h2>
                  <Button size="compact" onClick={() => setParam("aba", "mensagens")}>
                    Ver mensagens
                  </Button>
                </div>
                {recent.length === 0 ? (
                  <EmptyState
                    title="Nenhuma comunicação por enquanto"
                    description="As mensagens trocadas com este cliente aparecem aqui."
                  />
                ) : (
                  <ul className="cx-portal-feed">
                    {recent.map((message) => (
                      <li key={message.id}>
                        <p>{message.bodyPreview || message.bodyText}</p>
                        <small>
                          {hourOf(message.occurredAt)} ·{" "}
                          {message.direction === "outbound" ? "CosmosLeads" : "Você"}
                        </small>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <aside className="cx-card">
                <div className="card-title">
                  <h2>Suas pendências</h2>
                </div>
                {next ? (
                  <>
                    <div className="cx-portal-row">
                      <div>
                        <strong>{next.description}</strong>
                        <small>
                          {brDate(next.dueOn)} · {formatCents(invoiceBalanceCents(next, receipts))}
                        </small>
                      </div>
                    </div>
                    <Button onClick={() => setInvoiceOpen(next.id)}>Ver cobrança</Button>
                  </>
                ) : (
                  <p className="cx-note">Nenhuma cobrança pendente.</p>
                )}
                <div className="cx-portal-row">
                  <div>
                    <strong>Acesso demonstrativo</strong>
                    <small>{ACCESS_LABEL[access]}</small>
                  </div>
                </div>
                {access !== "active" && (
                  <Button onClick={() => setAccessOpen(true)}>
                    {access === "invited" ? "Simular ativação" : "Simular convite"}
                  </Button>
                )}
                <p className="cx-portal-demo">
                  Esta prévia não cria conta nem autenticação real.
                </p>
              </aside>
            </div>
          </>
        )}

        {view === "cobrancas" && (
          <>
            <div className="cx-portal-tools">
              <CompactSelect
                prefix="Situação"
                options={STATUS_FILTERS.map((item) => ({ value: item.value, label: item.label }))}
                value={status}
                onChange={(value: StatusFilter) =>
                  setParam("situacao", value === "all" ? null : value)
                }
              />
            </div>
            <BillingTable
              invoices={invoices.filter((invoice) =>
                matchesStatus(status, invoiceStatus(invoice, receipts, today)),
              )}
              receipts={receipts}
              today={today}
              onOpen={setInvoiceOpen}
            />
            <h2 className="cx-portal-heading">Contratos</h2>
            {contracts.length === 0 ? (
              <EmptyState title="Nenhum contrato registrado" />
            ) : (
              <ul className="cx-portal-contracts">
                {contracts.map((contract) => (
                  <li key={contract.id}>
                    <strong>{contract.description}</strong>
                    <small>
                      {formatCents(contract.amountCents)} ·{" "}
                      {contract.kind === "monthly" ? "Mensal" : "Avulso"} ·{" "}
                      {contract.closedOn
                        ? `fechado em ${brDate(contract.closedOn)}`
                        : "venda ainda não fechada"}
                      {contract.cancelledOn ? ` · encerrado em ${brDate(contract.cancelledOn)}` : ""}
                    </small>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {view === "mensagens" && (
          <section className="cx-card cx-portal-thread">
            <div className="cx-portal-tools">
              {threads.length > 0 && (
                <CompactSelect
                  prefix="Canal"
                  options={threads.map((thread) => ({
                    value: thread.id,
                    label: CHANNEL_LABEL[thread.channel] ?? thread.channel,
                  }))}
                  value={threadId}
                  onChange={(value: string) => setParam("conversa", value)}
                />
              )}
              <span className="cx-portal-demo">
                Leitura da conversa que já existe na operação; a prévia não envia nem recebe.
              </span>
            </div>
            <PortalMessages
              messages={messages
                .filter((message) => message.threadId === threadId)
                .slice()
                .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))}
            />
            <div className="cx-portal-compose">
              <input
                aria-label="Mensagem do cliente"
                placeholder="O cliente responderia por aqui…"
                disabled
                title="A prévia é interna: não há cliente conectado para escrever, e uma mensagem inventada entraria na caixa como se tivesse chegado de verdade."
              />
              <Button
                variant="primary"
                disabled
                title="A prévia é interna: não há acesso externo que possa enviar uma mensagem."
              >
                Enviar
              </Button>
            </div>
          </section>
        )}

        {view === "avisos" && (
          <section className="cx-card">
            <div className="card-title">
              <h2>Notificações do cliente</h2>
              <span className="hint">Somente as cobranças que o cliente acompanharia</span>
            </div>
            {notices.length === 0 ? (
              <EmptyState
                title="Nenhuma notificação nesta visão"
                description="Cobranças a vencer ou em atraso deste cliente apareceriam aqui."
              />
            ) : (
              <ul className="cx-portal-notices">
                {notices.map((notice: Notice) => (
                  <li key={notice.key}>
                    <div>
                      <strong>{notice.title}</strong>
                      <small>{notice.detail}</small>
                    </div>
                    <time dateTime={notice.occurredAt}>
                      {brDate(notice.occurredAt.slice(0, 10))}
                    </time>
                  </li>
                ))}
              </ul>
            )}
            {/* Ler e resolver são marcas da operação, na central de avisos: marcá-las
                daqui registraria como decisão do cliente algo que é do operador. */}
            <p className="cx-portal-demo">
              Ler e resolver pertencem à central de avisos da operação; esta prévia só mostra.
            </p>
          </section>
        )}
      </div>

      {openInvoice && (
        <InvoiceDrawer
          invoice={openInvoice}
          contract={contracts.find((contract) => contract.id === openInvoice.contractId)}
          today={today}
          onReceive={() => {
            setReceiptFor(openInvoice.id);
            setInvoiceOpen(null);
          }}
          onClose={() => setInvoiceOpen(null)}
        />
      )}

      {accessOpen && (
        <Dialog
          open
          onClose={() => setAccessOpen(false)}
          title={access === "invited" ? "Simular ativação" : "Simular convite"}
          footer={
            <>
              <Button variant="text" onClick={() => setAccessOpen(false)} disabled={savingAccess}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={advanceAccess} loading={savingAccess}>
                Confirmar simulação
              </Button>
            </>
          }
        >
          <p className="cx-note">
            {lead.name} · {lead.company}. Esta ação registra apenas o estado demonstrativo do
            acesso, sem enviar e-mail nem criar uma conta.
          </p>
        </Dialog>
      )}

      {receiptInvoice && (
        <ReceiptDialog
          invoice={receiptInvoice}
          contract={contracts.find((contract) => contract.id === receiptInvoice.contractId)}
          onClose={() => setReceiptFor(null)}
        />
      )}
    </div>
  );
}

function matchesStatus(filter: StatusFilter, status: InvoiceStatus): boolean {
  if (filter === "all") return true;
  if (filter === "cancelled") return status === "cancelled";
  if (filter === "paid") return status === "paid";
  if (filter === "late") return status === "overdue" || status === "partial_overdue";
  return status !== "paid" && status !== "cancelled";
}

function BillingTable({
  invoices,
  receipts,
  today,
  onOpen,
}: {
  invoices: readonly FinanceInvoice[];
  receipts: readonly FinanceReceipt[];
  today: string;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="cx-portal-table">
      <table>
        <caption className="cx-sr-only">
          Cobranças do cliente, com competência, vencimento, valor e situação.
        </caption>
        <thead>
          <tr>
            <th scope="col">Descrição</th>
            <th scope="col">Competência</th>
            <th scope="col">Vencimento</th>
            <th scope="col">Valor</th>
            <th scope="col">Situação</th>
            <th scope="col">Ação</th>
          </tr>
        </thead>
        <tbody>
          {invoices.length === 0 ? (
            <tr>
              <td colSpan={6}>Nenhuma cobrança nesta situação.</td>
            </tr>
          ) : (
            invoices.map((invoice) => {
              const status = invoiceStatus(invoice, receipts, today);
              return (
                <tr key={invoice.id}>
                  <td>
                    {invoice.description}
                    <small>
                      {invoice.kind === "extra"
                        ? "Extra"
                        : invoice.kind === "oneoff"
                          ? "Avulso"
                          : "Mensalidade"}
                    </small>
                  </td>
                  <td>{competence(invoice.dueOn)}</td>
                  <td>{brDate(invoice.dueOn)}</td>
                  <td className="num">{formatCents(invoice.amountCents)}</td>
                  <td>{INVOICE_STATUS_LABEL[status]}</td>
                  <td>
                    <Button size="compact" onClick={() => onOpen(invoice.id)}>
                      Detalhes
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function PortalMessages({
  messages,
}: {
  messages: ReadonlyArray<{
    id: string;
    direction: string;
    bodyText: string;
    occurredAt: string;
  }>;
}) {
  if (messages.length === 0) {
    return (
      <EmptyState
        title="Nenhuma mensagem nesta conversa"
        description="O que for trocado na operação aparece aqui do ponto de vista do cliente."
      />
    );
  }
  return (
    <div className="cx-portal-messages">
      {messages.map((message) => (
        // O lado inverte de propósito: aqui quem lê é o cliente, então o que
        // saiu da operação chega como recebido.
        <div
          key={message.id}
          className={`cx-msg-message ${message.direction === "outbound" ? "in" : "out"}`}
        >
          <div className="cx-msg-message-content">
            <div className="cx-msg-bubble">{message.bodyText}</div>
            <footer className="cx-msg-message-meta">
              {hourOf(message.occurredAt)} ·{" "}
              {message.direction === "outbound" ? "CosmosLeads" : "Você"}
            </footer>
          </div>
        </div>
      ))}
    </div>
  );
}

function InvoiceDrawer({
  invoice,
  contract,
  today,
  onReceive,
  onClose,
}: {
  invoice: FinanceInvoice;
  contract?: FinanceContract;
  today: string;
  onReceive: () => void;
  onClose: () => void;
}) {
  const store = useStore();
  const receipts = store.receipts ?? [];
  const balance = invoiceBalanceCents(invoice, receipts);
  const status = invoiceStatus(invoice, receipts, today);
  const own = receipts.filter((receipt) => receipt.invoiceId === invoice.id);

  return (
    <Drawer
      open
      onClose={onClose}
      title="Detalhes da cobrança"
      description={contract ? contract.description : undefined}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Fechar
          </Button>
          {balance > 0 && !invoice.cancelledAt && (
            <Button
              variant="primary"
              icon={<Receipt size={15} aria-hidden="true" />}
              onClick={onReceive}
            >
              Registrar recebimento
            </Button>
          )}
        </>
      }
    >
      <dl className="cx-portal-kv">
        <div>
          <dt>Descrição</dt>
          <dd>{invoice.description}</dd>
        </div>
        <div>
          <dt>Vencimento</dt>
          <dd className="num">{brDate(invoice.dueOn)}</dd>
        </div>
        <div>
          <dt>Valor</dt>
          <dd className="num">{formatCents(invoice.amountCents)}</dd>
        </div>
        <div>
          <dt>Saldo</dt>
          <dd className="num">{formatCents(balance)}</dd>
        </div>
        <div>
          <dt>Recebido</dt>
          <dd className="num">{formatCents(paidCents(receipts, invoice.id))}</dd>
        </div>
        <div>
          <dt>Situação</dt>
          <dd className="num">{INVOICE_STATUS_LABEL[status]}</dd>
        </div>
      </dl>

      <h3 className="cx-portal-heading">Recebimentos</h3>
      {own.length === 0 ? (
        <p className="cx-note">Nenhum recebimento registrado.</p>
      ) : (
        <ul className="cx-portal-receipts">
          {own.map((receipt) => (
            <li key={receipt.id}>
              <span>
                {brDate(receipt.receivedOn)} · {receipt.receivedAtTime}
              </span>
              <strong className="num">{formatCents(receipt.amountCents)}</strong>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
